// Kepabeanan › Dok Pabean: the customs document list (revamp PR 7a). Same URL state, list request (sort, status,
// branch, deleted toggle, paging), report and usage downloads, tours and copy as the Mantine page it replaces; the
// page is direction B (page header, toolbar card, Lumen table) on the PR 6a Doc Manifes model.
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Download, Loader2 } from 'lucide-react';
import { Suspense, useId, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';

import { PageState } from '@/components/charts';
import { TableSearch } from '@/components/data-table';
import { DateField } from '@/components/form/date-field';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList } from '@/components/lumen/breadcrumb';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { Label } from '@/components/lumen/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/lumen/select';
import { Switch } from '@/components/lumen/switch';
import { PageHeader, PageShell } from '@/components/shell/PageHeader';
import DocPabeanCreditsBanner from '@components/doc-pabean/DocPabeanCreditsBanner';
import DocPabeanTable from '@components/doc-pabean/doc-pabean-table';
import DownloadReportModal from '@components/doc-pabean/DownloadReportModal';
import { useDebouncedSearch } from '@components/doc-pabean/use-debounced-search';
import { BRANCH_ALL } from '@constants/branch';
import { useLanguage } from '@contexts/LanguageContext';
import usePersistentURL from '@hooks/use-persistent-url';
import axiosInstance from '@libs/axios';
import useGetBranchNames from './apis/use-cases/use-get-branch-names';
import useGetDocPabeans from './apis/use-cases/use-get-doc-pabeans';

type DocPabeanTableSectionProps = {
  currentPage: number;
  pageSize: number;
  searchQuery: string;
  statusFilter: string;
  orderBy: string;
  ascending: string;
  includeDeleted: boolean;
  branchFilter: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

function DocPabeanTableSection({
  currentPage,
  pageSize,
  searchQuery,
  statusFilter,
  orderBy,
  ascending,
  includeDeleted,
  branchFilter,
  onPageChange,
  onPageSizeChange,
}: DocPabeanTableSectionProps) {
  const { data: queryResult, isFetching } = useGetDocPabeans({
    page: currentPage,
    itemPerPage: pageSize,
    searchQuery,
    statusFilter,
    orderBy,
    ascending,
    includeDeleted,
    branchFilter,
  });

  const totalPage = pageSize > 0 ? Math.ceil(queryResult.totalCount / pageSize) : 0;

  return (
    <DocPabeanTable
      data={queryResult.data}
      numberPageSize={pageSize}
      numberPage={currentPage}
      loading={isFetching}
      setNumberPageSize={onPageSizeChange}
      totalPage={totalPage}
      onPageChange={onPageChange}
    />
  );
}

/** A select whose "none" choice is a real option (Radix items can't hold ''). */
const NONE = '__none';

function ToolbarSelect({
  placeholder,
  value,
  onChange,
  options,
  className,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <Select value={value === '' ? NONE : value} onValueChange={(v) => onChange(v === NONE ? '' : v)}>
      <SelectTrigger aria-label={placeholder} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value || NONE} value={o.value === '' ? NONE : o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function DocPabeanPage() {
  const { t } = useLanguage();
  const {
    currentPage,
    searchQuery,
    pageSize,
    statusFilter,
    sortBy,
    branchFilter,
    onPageChange,
    onSearchQueryChange,
    onPageSizeChange,
    onStatusFilterChange,
    onSortByChange,
    onBranchFilterChange,
  } = usePersistentURL();

  const { branches, hasMultipleBranches } = useGetBranchNames();
  const deletedId = useId();
  const usageStartId = useId();
  const usageEndId = useId();

  // Off by default: the list shows live documents exactly as before. Turning
  // it on surfaces rows deleted in Jarvis, whose CEISA filing still exists.
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [downloadModalOpened, setDownloadModalOpened] = useState(false);
  const [usageModalOpened, setUsageModalOpened] = useState(false);
  const [usageDownloading, setUsageDownloading] = useState(false);
  const [usageStartDate, setUsageStartDate] = useState<Date | null>(null);
  const [usageEndDate, setUsageEndDate] = useState<Date | null>(null);
  const [usageDocType, setUsageDocType] = useState<string | null>(null);
  const { reset } = useQueryErrorResetBoundary();

  // onSearchQueryChange resets the page to 1 itself.
  const [searchText, setSearchText] = useDebouncedSearch(searchQuery, onSearchQueryChange);

  const handleDownloadUsageReport = async () => {
    if (!usageStartDate || !usageEndDate) {
      toast.error(t('docpabean.errSelectDates'));
      return;
    }
    setUsageDownloading(true);
    try {
      const params = new URLSearchParams();
      if (usageDocType) params.append('docType', usageDocType);
      const queryString = params.toString();
      const response = await axiosInstance.post(
        `/api/v1/dashboard/usage-dashboard-detail${queryString ? `?${queryString}` : ''}`,
        {
          startDate: dayjs(usageStartDate).format('YYYY-MM-DD'),
          endDate: dayjs(usageEndDate).format('YYYY-MM-DD'),
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usage_dashboard_${dayjs(usageStartDate).format('YYYY-MM-DD')}_${dayjs(usageEndDate).format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('docpabean.successDownloaded'));
      setUsageModalOpened(false);
    } catch (error) {
      console.error('Error downloading usage report:', error);
      toast.error(t('docpabean.errDownload'));
    } finally {
      setUsageDownloading(false);
    }
  };

  const safeCurrentPage = currentPage > 0 ? currentPage : 1;
  const safePageSize = pageSize > 0 ? pageSize : 10;

  const statusOptions = [
    { value: '', label: t('docpabean.allStatus') },
    { value: 'BILLING', label: 'BILLING' },
    { value: 'SPPB', label: 'SPPB' },
    { value: 'SPTNP', label: 'SPTNP' },
    { value: 'BILLING SPTNP', label: 'BILLING SPTNP' },
    { value: 'NPE', label: 'NPE' },
    { value: '1', label: '1' },
    { value: 'UMUM', label: 'UMUM' },
    { value: 'SPJM', label: 'SPJM' },
  ];

  // '' is a real choice, not an absence: it sends no branch param, which the
  // API reads as "the caller's own branch".
  const branchOptions = [
    { value: '', label: t('docpabean.branchMine') },
    { value: BRANCH_ALL, label: t('docpabean.branchAll') },
    ...branches.map((branch) => ({ value: branch, label: branch })),
  ];

  // A single-office tenant has no filter to render, so ignore any branch left
  // in the URL — otherwise a link shared from a multi-branch tenant would
  // silently narrow their list with no visible control to undo it.
  const effectiveBranchFilter = hasMultipleBranches ? branchFilter : '';

  const sortOptions = [
    { value: '', label: t('docpabean.sortNewest') },
    { value: 'ajuNo:asc', label: t('docpabean.sortAjuAsc') },
    { value: 'ajuNo:desc', label: t('docpabean.sortAjuDesc') },
    { value: 'namaPerusahaan:asc', label: t('docpabean.sortShipperAsc') },
    { value: 'namaPerusahaan:desc', label: t('docpabean.sortShipperDesc') },
    { value: 'tanggalDaftar:asc', label: t('docpabean.sortDateOldest') },
    { value: 'tanggalDaftar:desc', label: t('docpabean.sortDateNewest') },
    { value: 'totalItem:asc', label: t('docpabean.sortItemLeast') },
    { value: 'totalItem:desc', label: t('docpabean.sortItemMost') },
  ];

  const [sortField, sortDirection] = sortBy.split(':');
  const orderBy = sortField || 'id';
  const ascending = sortDirection === 'asc' ? 'true' : 'false';

  const requiredMark = <span className="text-err"> *</span>;

  return (
    <div data-testid="doc-pabean-page">
      <PageShell>
        <PageHeader
          breadcrumbs={
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>{t('menu.kepabeanan')}</BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          }
          title={t('menu.docPabean')}
          actions={
            <>
              <Button variant="outline" onClick={() => setUsageModalOpened(true)}>
                <Download aria-hidden />
                {t('docpabean.downloadUsage')}
              </Button>
              <Button data-tour="docpabean-report" onClick={() => setDownloadModalOpened(true)}>
                <Download aria-hidden />
                {t('docpabean.downloadReport')}
              </Button>
            </>
          }
        />

        <DocPabeanCreditsBanner />

        <div className="flex min-w-0 flex-col gap-3 overflow-hidden rounded-card border border-line bg-raised p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2" data-tour="docpabean-filters">
              <TableSearch
                value={searchText}
                onChange={setSearchText}
                placeholder={t('lumen.table.search')}
                className="w-full sm:w-72"
              />
              {/* The sort and status selects open on their "none" choice, as the old clearable selects did. */}
              <ToolbarSelect
                placeholder={t('docpabean.sortPlaceholder')}
                value={sortBy}
                onChange={onSortByChange}
                options={sortOptions}
                className="w-full sm:w-52"
              />
              <ToolbarSelect
                placeholder={t('docpabean.filterStatus')}
                value={statusFilter}
                onChange={onStatusFilterChange}
                options={statusOptions}
                className="w-full sm:w-44"
              />
              {hasMultipleBranches && (
                <ToolbarSelect
                  placeholder={t('docpabean.filterBranch')}
                  value={branchFilter}
                  onChange={onBranchFilterChange}
                  options={branchOptions}
                  className="w-full sm:w-44"
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch id={deletedId} checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
              <Label htmlFor={deletedId} className="whitespace-nowrap font-normal text-fg text-sm">
                {t('docpabean.showDeleted')}
              </Label>
            </div>
          </div>

          <ErrorBoundary
            onReset={reset}
            resetKeys={[
              safeCurrentPage,
              safePageSize,
              searchQuery,
              statusFilter,
              sortBy,
              includeDeleted,
              effectiveBranchFilter,
            ]}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <PageState
                kind="error"
                framed={false}
                className="min-h-64"
                title={(error as Error).message || t('docpabean.errorLoad')}
                onRetry={resetErrorBoundary}
                retryLabel={t('docpabean.retry')}
              />
            )}
          >
            <Suspense
              fallback={
                <PageState kind="loading" framed={false} className="min-h-64" title={t('lumen.table.loading')} />
              }
            >
              <div data-tour="docpabean-table">
                <DocPabeanTableSection
                  currentPage={safeCurrentPage}
                  pageSize={safePageSize}
                  searchQuery={searchQuery}
                  statusFilter={statusFilter}
                  orderBy={orderBy}
                  ascending={ascending}
                  includeDeleted={includeDeleted}
                  branchFilter={effectiveBranchFilter}
                  onPageChange={(newPage) => onPageChange(Math.max(1, newPage))}
                  onPageSizeChange={onPageSizeChange}
                />
              </div>
            </Suspense>
          </ErrorBoundary>
        </div>

        <DownloadReportModal
          opened={downloadModalOpened}
          onClose={() => setDownloadModalOpened(false)}
          branchFilter={effectiveBranchFilter}
        />

        <Dialog open={usageModalOpened} onOpenChange={setUsageModalOpened}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download aria-hidden className="size-[18px]" />
                {t('docpabean.downloadUsage')}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor={usageStartId}>
                  {t('docpabean.startDate')}
                  {requiredMark}
                </Label>
                <DateField
                  id={usageStartId}
                  placeholder={t('docpabean.selectStartDate')}
                  value={usageStartDate}
                  onChange={setUsageStartDate}
                  clearable
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={usageEndId}>
                  {t('docpabean.endDate')}
                  {requiredMark}
                </Label>
                <DateField
                  id={usageEndId}
                  placeholder={t('docpabean.selectEndDate')}
                  value={usageEndDate}
                  onChange={setUsageEndDate}
                  clearable
                  required
                  minDate={usageStartDate || undefined}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t('docpabean.docType')}</Label>
                <ToolbarSelect
                  placeholder={t('docpabean.all')}
                  value={usageDocType ?? ''}
                  onChange={(v) => setUsageDocType(v || null)}
                  options={[
                    { value: '', label: t('docpabean.all') },
                    { value: 'PIB', label: 'PIB' },
                    { value: 'PEB', label: 'PEB' },
                  ]}
                  className="w-full"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUsageModalOpened(false)} disabled={usageDownloading}>
                {t('docpabean.cancel')}
              </Button>
              <Button
                onClick={handleDownloadUsageReport}
                disabled={!usageStartDate || !usageEndDate || usageDownloading}
              >
                {usageDownloading ? (
                  <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
                ) : (
                  <Download aria-hidden />
                )}
                {t('docpabean.download')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageShell>
    </div>
  );
}
